import { useState, useEffect, useCallback } from 'react';
import { PTT_CONFIG } from '@/lib/constants';

interface HIDDeviceHook {
  device: HIDDevice | null;
  isAvailable: boolean;
  requestDevice: () => Promise<HIDDevice | null>;
  onButtonPress: (callback: () => void) => void;
  onButtonRelease: (callback: () => void) => void;
}

export function useHIDDevice(): HIDDeviceHook {
  const [device, setDevice] = useState<HIDDevice | null>(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [pressCallback, setPressCallback] = useState<(() => void) | null>(null);
  const [releaseCallback, setReleaseCallback] = useState<(() => void) | null>(null);

  // Check if WebHID is available
  useEffect(() => {
    setIsAvailable('hid' in navigator);
  }, []);

  // Request HID device
  const requestDevice = useCallback(async (): Promise<HIDDevice | null> => {
    if (!('hid' in navigator)) {
      console.warn('WebHID not supported');
      return null;
    }

    try {
      const devices = await navigator.hid!.requestDevice({
        filters: [...PTT_CONFIG.hidFilters],
      });

      if (devices.length === 0) {
        console.log('No HID device selected');
        return null;
      }

      const selectedDevice = devices[0];
      await selectedDevice.open();

      console.log('HID device connected:', selectedDevice.productName);
      setDevice(selectedDevice);

      return selectedDevice;
    } catch (error) {
      console.error('Failed to request HID device:', error);
      return null;
    }
  }, []);

  // Set up event listeners when device is connected
  useEffect(() => {
    if (!device) return;

    const handleInputReport = (event: HIDInputReportEvent) => {
      const { data } = event;
      const bytes = new Uint8Array(data.buffer);

      // Parse button state (device-specific logic)
      // Generic: any non-zero byte indicates button press
      const isPressed = bytes.some(b => b !== 0);

      if (isPressed && pressCallback) {
        pressCallback();
      } else if (!isPressed && releaseCallback) {
        releaseCallback();
      }
    };

    device.addEventListener('inputreport', handleInputReport as EventListener);

    return () => {
      device.removeEventListener('inputreport', handleInputReport as EventListener);
    };
  }, [device, pressCallback, releaseCallback]);

  // Register press callback
  const onButtonPress = useCallback((callback: () => void) => {
    setPressCallback(() => callback);
  }, []);

  // Register release callback
  const onButtonRelease = useCallback((callback: () => void) => {
    setReleaseCallback(() => callback);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (device && device.opened) {
        device.close().catch(console.error);
      }
    };
  }, [device]);

  return {
    device,
    isAvailable,
    requestDevice,
    onButtonPress,
    onButtonRelease,
  };
}
